<template>
  <v-carousel v-model="model" delimiter-icon="mdi-circle" class="hero-carousel background" height="100vh" cycle
    interval="12000000" hide-delimiter-background show-arrows-on-hover>

    <!-- ─── Slide 1: Who We Are ──────────────────────────────────────────────── -->
    <v-carousel-item>
      <div class="hero-slide about-slide">
        <div class="gradient-overlay"></div>
        <v-container class="fill-height">
          <v-row class="fill-height" align="center" justify="center">
            <v-col lg="11" md="11" sm="12" cols="12" class="px-5">
              <v-row class="fill-height" align="center" justify="space-between">
                <v-col lg="6" md="6" sm="12" cols="12" class="text-content" data-aos="fade-right">
                  <div class="hero-badge mb-4">
                    <v-chip color="primary" outlined small class="pulse-animation">
                      <v-icon small left>mdi-star-circle</v-icon>
                      {{ $t('brand.name') }}
                    </v-chip>
                  </div>
                  <h1 class="hero-title text-h2 font-weight-bold mb-6">
                    {{ $t('brand.name') }}
                  </h1>
                  <p class="hero-description text-h5 mb-5 font-weight-light">
                    {{ $t('brand.desc') }}
                  </p>
                  <p class="hero-subdescription text-h6 mb-8 font-weight-regular">
                    {{ $t('landing.whoWeAreDesc') }}
                  </p>
                  <div class="cta-buttons">
                    <v-btn x-large color="primary" elevation="8" class="mb-3" rounded @click="navigateToContact">
                      <v-icon left>mdi-rocket-launch</v-icon>
                      {{ $t('landing.getStarted') }}
                    </v-btn>
                    <v-btn x-large outlined color="primary" class="mb-3" rounded @click="handleVideoOpen">
                      <v-icon left>mdi-play-circle</v-icon>
                      {{ $t('landing.watchVideo') }}
                    </v-btn>
                  </div>
                  <div class="hero-stats mt-10">
                    <v-row>
                      <v-col cols="4" class="text-center">
                        <div class="stat-number primary--text text-h4 font-weight-bold">20+</div>
                        <div class="stat-label text-caption">{{ $t('landing.projects') }}</div>
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <div class="stat-number primary--text text-h4 font-weight-bold">7+</div>
                        <div class="stat-label text-caption">{{ $t('landing.clients') }}</div>
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <div class="stat-number primary--text text-h4 font-weight-bold">5+</div>
                        <div class="stat-label text-caption">{{ $t('landing.years') }}</div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
                <v-col lg="5" md="5" sm="12" cols="12" class="text-center" data-aos="fade-left">
                  <div class="hero-image-wrapper">
                    <ecosystem-orbit />
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-carousel-item>

    <!-- ─── Slide 2: Solutions Architecture ────────────────────────────────────── -->
    <v-carousel-item>
      <div class="hero-slide ecosystem-slide">
        <div class="gradient-overlay gradient-alt"></div>
        <v-container class="fill-height">
          <v-row class="fill-height" align="center" justify="center">
            <v-col lg="11" md="11" sm="12" cols="12" class="px-5">
              <v-row class="fill-height" align="center" justify="space-between">

                <!-- Left: copy -->
                <v-col lg="5" md="5" sm="12" cols="12" class="text-content" data-aos="fade-right">
                  <div class="hero-badge mb-4">
                    <v-chip color="primary" outlined small class="pulse-animation">
                      <v-icon small left>mdi-layers-triple-outline</v-icon>
                      {{ $t('landing.ecosystemKicker') }}
                    </v-chip>
                  </div>
                  <h2 class="hero-title text-h2 font-weight-bold mb-5">
                    {{ $t('landing.ecosystemTitle') }}
                  </h2>
                  <p class="hero-description text-h6 mb-6 font-weight-light">
                    {{ $t('landing.ecosystemDesc') }}
                  </p>
                  <p class="hero-subdescription mb-8">
                    {{ $t('landing.ecosystemMarketplace') }}
                  </p>
                  <div class="cta-buttons">
                    <v-btn x-large color="primary" elevation="8" rounded :to="localePath('/services/website')">
                      <v-icon left>mdi-laptop</v-icon>
                      {{ $t('landing.ecosystemCta') }}
                    </v-btn>
                  </div>
                </v-col>

                <!-- Right: Solution domains grid -->
                <v-col lg="6" md="6" sm="12" cols="12" class="text-center" data-aos="fade-left">
                  <div class="ecosystem-grid p-1">
                    <div
                      v-for="(sol, i) in solutionDomains"
                      :key="i"
                      class="eco-app-card"
                      :style="{ '--app-color': sol.color }"
                    >
                      <div class="eco-app-icon">
                        <v-icon :color="sol.color" size="32">{{ sol.icon }}</v-icon>
                      </div>
                      <div class="eco-app-name">{{ $t(sol.titleKey) }}</div>
                      <div class="eco-app-desc">{{ $t(sol.descKey) }}</div>
                      <v-btn
                        x-small
                        rounded
                        outlined
                        :color="sol.color"
                        class="mt-3 eco-app-btn"
                        :to="localePath(sol.link)"
                      >
                        {{ $t('landing.explorer') }}
                        <v-icon x-small right>{{ $vuetify.rtl ? 'mdi-arrow-left' : 'mdi-arrow-right' }}</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-col>

              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-carousel-item>

    <!-- ─── Slide 3: Build With Us ────────────────────────────────────────────── -->
    <v-carousel-item>
      <div class="hero-slide services-slide">
        <div class="gradient-overlay gradient-alt"></div>
        <v-container class="fill-height">
          <v-row class="fill-height" align="center" justify="center">
            <v-col lg="11" md="11" sm="12" cols="12" class="px-5">
              <v-row class="fill-height" align="center" justify="space-between">

                <!-- Left: service cards grid -->
                <v-col lg="6" md="6" sm="12" cols="12" :order="$vuetify.breakpoint.smAndDown ? 2 : 1" data-aos="fade-right">
                  <div class="services-grid">
                    <div
                      v-for="service in services"
                      :key="service.key"
                      class="service-pill"
                    >
                      <div class="service-pill-icon">
                        <v-icon :color="service.color" size="28">{{ service.icon }}</v-icon>
                      </div>
                      <div class="service-pill-body">
                        <div class="service-pill-title">{{ $t(`landing.service_${service.key}`) }}</div>
                        <div class="service-pill-desc">{{ $t(`landing.service_${service.key}_desc`) }}</div>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Right: copy -->
                <v-col lg="5" md="5" sm="12" cols="12" :order="$vuetify.breakpoint.smAndDown ? 1 : 2" class="text-content" data-aos="fade-left">
                  <div class="hero-badge mb-4">
                    <v-chip color="accent" outlined small class="pulse-animation">
                      <v-icon small left>mdi-wrench-outline</v-icon>
                      {{ $t('landing.servicesKicker') }}
                    </v-chip>
                  </div>
                  <h2 class="hero-title text-h2 font-weight-bold mb-5">
                    {{ $t('landing.servicesTitle') }}
                  </h2>
                  <p class="hero-description text-h6 mb-6 font-weight-light">
                    {{ $t('landing.servicesDesc') }}
                  </p>
                  <p class="hero-subdescription mb-8">
                    {{ $t('landing.servicesCustomNote') }}
                  </p>
                  <div class="cta-buttons">
                    <v-btn x-large color="primary" elevation="8" rounded @click="navigateToContact">
                      <v-icon left>mdi-chat-outline</v-icon>
                      {{ $t('landing.servicesCta') }}
                    </v-btn>
                  </div>
                </v-col>

              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-carousel-item>

    <!-- Video Dialog -->
    <VideoDialog v-model="videoDialog" :title="$t('landing.watchVideo')"
      video-url="https://www.youtube.com/embed/OqBJfeWRuvI?si=1RowSkG8NBQymrOK" />

    <!-- Scroll Indicator -->
    <div class="scroll-indicator" @click="scrollDown">
      <div class="scroll-dot"></div>
    </div>
  </v-carousel>
</template>

<style scoped lang="scss">
@import "./slider-styles";
</style>

<script>
import VideoDialog from '~/components/VideoDialog.vue';
import EcosystemOrbit from '~/components/hero/EcosystemOrbit.vue';

export default {
  components: {
    VideoDialog,
    EcosystemOrbit
  },
  props: {
    apps: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    model: 0,
    videoDialog: false,
    solutionDomains: [
      {
        titleKey: 'landing.service_mobile',
        descKey: 'landing.service_mobile_desc',
        icon: 'mdi-cellphone-check',
        color: '#E53935',
        link: '/services/mobile'
      },
      {
        titleKey: 'landing.service_website',
        descKey: 'landing.service_website_desc',
        icon: 'mdi-web',
        color: '#00897B',
        link: '/services/website'
      },
      {
        titleKey: 'landing.service_desktop',
        descKey: 'landing.service_desktop_desc',
        icon: 'mdi-monitor-screenshot',
        color: '#7C3AED',
        link: '/services/desktop'
      },
      {
        titleKey: 'landing.service_plugin',
        descKey: 'landing.service_plugin_desc',
        icon: 'mdi-puzzle-outline',
        color: '#3F51B5',
        link: '/services/plugin'
      }
    ],
    services: [
      { key: 'mobile',  icon: 'mdi-cellphone-check',       color: '#E53935' },
      { key: 'website', icon: 'mdi-web',                   color: '#00897B' },
      { key: 'desktop', icon: 'mdi-monitor-screenshot',    color: '#7C3AED' },
      { key: 'plugin',  icon: 'mdi-puzzle-outline',        color: '#3F51B5' },
    ]
  }),
  computed: {
    locale() {
      return this.$i18n.locale;
    }
  },
  methods: {
    handleVideoOpen() {
      this.videoDialog = true;
    },
    navigateToContact() {
      this.$router.push(this.localePath('/contact'));
    },
    scrollDown() {
      window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
    }
  }
};
</script>

